import React, { useMemo, useRef, useState } from 'react';
import TopFilterGroup from '../../../common/components/TopFilter/components/TopFilterGroup.container';
import { useFilters } from '../../../common/hooks/useFilters';
import { DataTable } from '@toluna-ui-toolkit/data-table';
import { DISPLAY_BUTTON_POSITION, TOP_FILTER_CARD_SIZE } from '../../../common/components/TopFilter/utils/constants';
import NoDataAvailable from '../../../common/components/NoDataAvailable/NoDataAvailable';
import TextHighlighter from '../components/TextHighlighter';
import SearchInput from '../../../common/components/SearchInput/components/SearchInput';
import FileUpload from '../components/FileUpload';
import * as SC from './BrandUpload.styles';
import { CheckIcon } from '../../../assets/svg-icons/CheckIcon';
import { UnCheckIcon } from '../../../assets/svg-icons/UncheckIcon';
import { toHeaderLabel } from '../utils/utils';
import { TRANSLATION_TEXT } from '../../../utils/translations';
import { PRESIGNED_IMPORTY_TYPE_ID } from '../utils/utils';
import { logFileEntryBrandData, clearCacheBrandData } from '../../../services/apiServer.service';
import { useOnClickOutside } from '../../../common/hooks/useOnClickOutside';
import ReusableModal from '../../../common/components/ReusableModal/ReusableModal';
import ExportButton from '../../../common/components/ExportButton/components/ExportButton.container';
const tableKey = {
    brandName: 'brandName',
    brandCode: 'brandCode',
    countryId: 'countryId',
    color: 'color',
};

const startUploadProcessStep = async operation => {
    try {
        const response = await operation.request();
        operation.resolve(response);

        if (operation.next) {
            await startUploadProcessStep(operation.next);
        }
    } catch (error) {
        operation.reject();
    }
};
const BrandUpload = ({
    data,
    getData,
    filterSelected,
    globalLoading,
    filtersThrownError,
    startClearCacheLoading,
    endClearCacheLoading,
    showErrorNotification,
}) => {
    useFilters();
    const modalRef = useRef();
    const [isOpen, setIsOpen] = useState(false);
    useOnClickOutside(modalRef, () => setIsOpen(false));

    const buildColumns = tableData => {
        if (!tableData?.length) return [];

        return Object.keys(tableData[0])
            .filter(key => key !== tableKey.countryId)
            .map(key => {
                if (key === tableKey.brandName) {
                    return {
                        Header: <div>{toHeaderLabel(key)}</div>,
                        accessor: key,
                        minWidth: 250,
                        maxWidth: 300,
                        Cell: ({ cell: { value }, state: { globalFilter } }) => (
                            <TextHighlighter title={value} entireString={value} stringToHighlight={globalFilter} />
                        ),
                    };
                }

                if (key === tableKey.brandCode) {
                    return {
                        Header: <div>{toHeaderLabel(key)}</div>,
                        accessor: key,
                        minWidth: 100,
                        maxWidth: 150,
                        Cell: ({ cell: { value } }) => <span>{value} </span>,
                    };
                }
                if (key === tableKey.color) {
                    return {
                        Header: <div>{toHeaderLabel(key)}</div>,
                        accessor: key,
                        minWidth: 100,
                        maxWidth: 150,
                        Cell: ({ cell: { value } }) => (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                {value} <SC.BrandColorBlock color={value} />
                            </div>
                        ),
                    };
                }

                return {
                    Header: <div>{toHeaderLabel(key)}</div>,
                    accessor: key,
                    minWidth: 70,
                    maxWidth: 150,
                    disableGlobalFilter: true,
                    disableSortBy: true,
                    Cell: ({ cell: { value } }) =>
                        typeof value === 'boolean' ? value ? <CheckIcon /> : <UnCheckIcon /> : <span>{value}</span>,
                };
            });
    };

    const columns = useMemo(() => buildColumns(data), [data]);

    const handleClearCache = async () => {
        const operation = {
            request: () => {
                startClearCacheLoading();
                return clearCacheBrandData(filterSelected);
            },
            resolve: () => {
                endClearCacheLoading();
            },
            reject: () => {
                endClearCacheLoading();
                showErrorNotification(true);
            },
        };
        await startUploadProcessStep(operation);
    };

    return (
        <div style={{ position: 'relative' }}>
            {!filtersThrownError ? (
                <TopFilterGroup
                    cardSize={TOP_FILTER_CARD_SIZE.SMALL}
                    displayButtonPosition={DISPLAY_BUTTON_POSITION.BOTTOM}
                />
            ) : (
                <NoDataAvailable show={true} />
            )}

            <SC.BrandUploadButtonsContainer>
                <div style={{ display: 'flex', gap: '15px' }}>
                    <FileUpload
                        oneButton
                        customStyle={{ marginLeft: '0px' }}
                        importTypeId={PRESIGNED_IMPORTY_TYPE_ID.BRANDDATA}
                        loggerFunction={logFileEntryBrandData}
                        type={'text/csv'}
                        filterSelected={filterSelected}
                        disabled={!filterSelected.length}
                        onUploadSuccess={() => getData()}
                    />
                    <ExportButton
                        text={TRANSLATION_TEXT.EXPORT_TEXT}
                        exportDisabled={!filterSelected.length}
                        isTextButton={true}
                    />
                </div>
                <SC.Button onClick={() => setIsOpen(true)} disabled={!filterSelected.length}>
                    <p>{TRANSLATION_TEXT.CLEAR_CACHE_TEXT}</p>
                </SC.Button>
                {isOpen ? (
                    <ReusableModal
                        modalWrapperRef={modalRef}
                        yesText={TRANSLATION_TEXT.YES_TEXT}
                        cancelText={TRANSLATION_TEXT.CANCEL_TEXT}
                        singleButton={false}
                        text={TRANSLATION_TEXT.CLEAR_CACHE_MODAL_TEXT}
                        isAbleToDismiss={true}
                        title={''}
                        onClose={() => {
                            setIsOpen(false);
                        }}
                        onApply={() => {
                            setIsOpen(false);
                            handleClearCache();
                        }}
                        modalWidth={'850px'}
                        modalHeight={'350px'}
                    />
                ) : null}
            </SC.BrandUploadButtonsContainer>

            {data && !globalLoading ? (
                <SC.BackOfficeTableWrapper>
                    <DataTable
                        styles={SC.TABLE_STYLE}
                        isPaginated={false}
                        minRows={0}
                        isLoading={!data}
                        data={data || []}
                        columns={columns}
                        FilterControlComponent={
                            <SearchInput
                                customStyle={SC.customStyle}
                                placeholder={TRANSLATION_TEXT.SEARCH_BRAND_NAME_TEXT}
                                className={'table-search'}
                                isForTable
                            />
                        }
                        isFilterable={true}
                        isScrollable={false}
                        NonSortedArrow={SC.NonSorted}
                        SortedAscArrow={SC.SortedAsc}
                        SortedDescArrow={SC.SortedDesc}
                    />
                </SC.BackOfficeTableWrapper>
            ) : (
                <NoDataAvailable show={true} />
            )}
        </div>
    );
};

export default BrandUpload;
